import { connectExternalSource } from '@wix/media-manager-sdk';
import { concat, contains, filter, head, indexOf, insert, map, partition, pipe, replace, uniq, zipObj } from 'ramda';
import { isString } from 'ramda-adjunct';
import { useCallback, useEffect, useReducer } from 'react';
import { ModalName } from '../../../constants/ModalName';
import { ExternalSource } from '../../../constants/Source';
import { useMMGRSettings } from '../../../utils/hoc/withMMGRSettings';
import { wixComDomainStatic } from '../../../utils/hoc/withMMGRSettings/defaultMMGRSettings';
import { TMMGRSettings } from '../../../utils/hoc/withMMGRSettings/withMMGRSettings.types';
import { useTrackAddMediaAccountAnalytics } from '../../../utils/hooks/bi/useTrackAddMediaAccountAnalytics';
import { useModalHandlersAndOptions } from '../../../utils/hooks/useModal';
import { MediaManagerSDKConnector } from '../../../utils/media-manager-sdk';
import { ExternalSourcesConfigs } from '../constants/Sources';
import { TGetConnectedSources } from '../FilesImporter.types';

const REDIRECT_URL_BASE =
  process.env.NODE_ENV === 'development' ? window.__STATICS_BASE_URL__ : `${window.location.origin}/media-manager/`;

interface IsConnectedSource {
  isConnected: boolean;
  source: ExternalSource;
}

const getConnectedSources: TGetConnectedSources = async (sources, connectorsMap, permanentSources) => {
  return Promise.all<IsConnectedSource>(
    map(async (source) => {
      if (connectorsMap[source]) {
        return connectorsMap[source]
          .isConnected()
          .catch(() => false)
          .then((isConnected) => ({ isConnected, source }));
      }

      return {
        source,
        isConnected: false,
      };
    }, sources),
  ).then(
    pipe<IsConnectedSource[], IsConnectedSource[], ExternalSource[], ExternalSource[], ExternalSource[]>(
      // Some sources are not in media-manager-sdk (like Link), so we filter them out
      filter(({ isConnected }) => isConnected),
      map(({ source }) => source),
      concat(permanentSources),
      uniq,
    ),
  );
};

export const validateAdditionalSources = (externalSources: ExternalSource[], mmgrSettings): ExternalSource[] =>
  // TODO: added on Apr 2nd, remove after presets from kit are updated everywhere
  !contains(ExternalSource.Vimeo, externalSources)
    ? insert(indexOf(ExternalSource.Flickr, externalSources), ExternalSource.Vimeo, externalSources)
    : externalSources;
const getInitialState = ({
  defaultSource,
  mmgrSettings,
}: {
  defaultSource: ExternalSource;
  mmgrSettings: TMMGRSettings;
}) => {
  const { externalSources, pixAPI } = mmgrSettings;
  const sources = validateAdditionalSources(externalSources, mmgrSettings);
  const selectedSource: ExternalSource = defaultSource || head(sources);
  const [connectableSources, permanentSources] = partition(
    (source) => Boolean(ExternalSourcesConfigs[source].mediaManagerSDKName),
    sources,
  );

  const connectors = map<ExternalSource, MediaManagerSDKConnector>(
    (source) =>
      connectExternalSource(ExternalSourcesConfigs[source].mediaManagerSDKName, {
        apiUrl: pixAPI,
        redirectUrl: `${REDIRECT_URL_BASE}assets/service-connected.html`,
        disableXsrf: false,
      }),
    connectableSources,
  );

  const connectorsMap = zipObj(connectableSources, connectors);

  return {
    connectorsMap,
    selectedSource,
    connectedSources: [],
    permanentSources,
    isSelectedSourceConnected: false,
  };
};

const enum ActionType {
  SetSource = 'set-source',
  SetConnectedSources = 'set-connected-sources',
}

const reducer = (state, action) => {
  switch (action.type) {
    case ActionType.SetSource:
      const { connectedSources } = state;
      const { source } = action.payload;
      return {
        ...state,
        selectedSource: source,
        isSelectedSourceConnected: contains(source, connectedSources),
      };

    case ActionType.SetConnectedSources: {
      const { selectedSource } = state;
      const { connectedSources } = action.payload;
      return { ...state, connectedSources, isSelectedSourceConnected: contains(selectedSource, connectedSources) };
    }

    default:
      return state;
  }
};

export const useSourceState = () => {
  const {
    additionalOptions: { defaultSource },
  } = useModalHandlersAndOptions(ModalName.FilesImporter);

  const { mmgrSettings } = useMMGRSettings();
  const { externalSources } = mmgrSettings;
  const sources = validateAdditionalSources(externalSources, mmgrSettings);
  const [state, dispatch] = useReducer(reducer, getInitialState({ defaultSource, mmgrSettings }));

  const postMessageListener = useCallback(({ data, origin }) => {
    const originNormalizationRegex = /^(https?:)?\/\//;
    const thisOrigin = replace(originNormalizationRegex, '', wixComDomainStatic);
    const eventOrigin = replace(originNormalizationRegex, '', origin);

    if (eventOrigin !== thisOrigin) {
      return;
    }

    if (!isString(data) || !data.match(/status=OK/)) {
      return;
    }

    window.connectService(data);
  }, []);

  useEffect(() => {
    window.addEventListener('message', postMessageListener);

    return () => {
      window.removeEventListener('message', postMessageListener);
    };
  }, []);

  const changeSource = useCallback((source: ExternalSource) => {
    dispatch({ type: ActionType.SetSource, payload: { source } });
  }, []);

  const { connectorsMap, connectedSources, permanentSources } = state;

  const { trackAddMediaAccountConnectDisconnected, trackAddMediaAccountConnectConnected } =
    useTrackAddMediaAccountAnalytics();

  const refreshSourcesState = useCallback(async (sources) => {
    const connectedSources = await getConnectedSources(sources, connectorsMap, permanentSources);

    dispatch({ type: ActionType.SetConnectedSources, payload: { connectedSources } });
  }, []);

  useEffect(() => {
    refreshSourcesState(validateAdditionalSources(mmgrSettings.externalSources, mmgrSettings));
  }, []);

  const connect = useCallback(
    (source) => {
      const sourceConnector = connectorsMap[source];
      if (!sourceConnector) {
        return;
      }

      sourceConnector.connect().then(() => {
        trackAddMediaAccountConnectConnected(source);
        refreshSourcesState(sources);
      });
    },
    [connectorsMap, sources],
  );

  const disconnect = useCallback(
    (source) => {
      const sourceConnector = connectorsMap[source];
      if (!sourceConnector) {
        return;
      }

      if (contains(source, connectedSources)) {
        sourceConnector.disconnect().then(() => {
          trackAddMediaAccountConnectDisconnected(source);
          refreshSourcesState(sources);
        });
      }
    },
    [connectorsMap, connectedSources],
  );

  return {
    ...state,
    connect,
    disconnect,
    changeSource,
  };
};
